import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { EnvelopeOpen, CheckCircle, XCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { track } from "../lib/analytics";

export default function IntroInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/my/intro-requests");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const respond = async (id, accept) => {
    try {
      await api.post(`/my/intro-requests/${id}/respond`, { accept });
      if (accept) track("intro_accepted", { request_id: id });
      toast.success(accept ? "Intro accepted, contact shared" : "Intro declined");
      load();
    } catch (e) {
      toast.error("Could not respond");
    }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <section className="mt-12" data-testid="intro-inbox">
      <div className="flex items-center gap-2 mb-4">
        <EnvelopeOpen weight="duotone" className="w-6 h-6 text-slate-700" />
        <h2 className="font-serif-display text-2xl text-slate-900">Attorney Intro Requests</h2>
        <span className="label-tiny">{items.length} total</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        {items.map((r, i) => {
          const lp = r.lawyer_preview?.lawyer_profile || {};
          const statusIcon = r.status === "accepted" ? CheckCircle : r.status === "declined" ? XCircle : Clock;
          const statusColor = r.status === "accepted" ? "text-emerald-700" : r.status === "declined" ? "text-red-700" : "text-amber-700";
          const SIcon = statusIcon;
          return (
            <div key={r.id} className={`border border-slate-200 ${i % 2 === 1 ? "md:border-l-0" : ""} -mt-px md:mt-0 p-5`} data-testid={`intro-request-${r.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="font-serif-display text-lg text-slate-900">{r.lawyer_preview?.name || "Attorney"}</div>
                  <div className="text-xs text-slate-500">{lp.firm_name || "—"} · {lp.bar_state || ""}</div>
                </div>
                <div className="flex items-center gap-1">
                  <SIcon className={`w-4 h-4 ${statusColor}`} /> <span className={`label-tiny ${statusColor}`}>{r.status}</span>
                </div>
              </div>
              <div className="text-sm text-slate-600">Regarding case: <span className="font-medium">{r.case_name || r.case_id}</span></div>
              {lp.bio && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{lp.bio}</p>}
              {r.status === "pending" && (
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-none border-slate-300" onClick={() => respond(r.id, false)} data-testid={`decline-${r.id}`}>Decline</Button>
                  <Button size="sm" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white" onClick={() => respond(r.id, true)} data-testid={`accept-${r.id}`}>Accept & share contact</Button>
                </div>
              )}
              {r.status === "accepted" && r.lawyer_contact && (
                <div className="mt-3 border-l-2 border-emerald-400 pl-3 text-sm">
                  Contact shared: <a className="underline" href={`mailto:${r.lawyer_contact.email}`}>{r.lawyer_contact.email}</a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
